import { useEffect, useRef, useState } from "react";

type CountToProps = {
  target?: number;
  isReady?: boolean;
  duration?: number;
  decimals?: number;
};

export const useCountTo = ({ target, isReady, duration = 500, decimals = 0 }: CountToProps) => {
  const multiplier = Math.pow(10, decimals);
  const product = (target ?? 0) * multiplier;
  const [value, setValue] = useState(product);
  const raf = useRef<number | undefined>(undefined);
  const prevRef = useRef<number>(product);

  useEffect(() => {
    if (!isReady || typeof product !== "number") return;

    const from = prevRef.current;
    const start = performance.now();
    const isDecreasing = product < from;

    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const raw = from + (product - from) * progress;

      // clamp to avoid overshoot / -1 flashes
      const next = isDecreasing ? Math.max(Math.ceil(raw), product) : Math.min(Math.floor(raw), product);

      setValue(next);

      if (progress < 1) {
        raf.current = requestAnimationFrame(tick);
      } else {
        prevRef.current = product; // store last value for next animation
      }
    };

    raf.current = requestAnimationFrame(tick);

    return () => {
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [product, isReady, duration]);

  return value / multiplier;
};
